import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Checkbox,
  Col,
  Dropdown,
  Modal,
  Row,
  Space,
  Table,
  Tooltip
} from 'antd'
import styled from 'styled-components'
import produce from 'immer'
import _ from 'lodash'

import { DownOutlined } from '@ant-design/icons'
import { colours, sizes } from '@qflow/theme'

import {
  DetailRow,
  AttachmentsSection,
  ValueAdd
} from 'portal/components/RecordDetails'
import { AttachmentsHeader } from 'portal/components/RecordDetails/AttachmentsHeader'

import {
  AlertsPanel,
  RemoveRecordButton,
  EditRecordButton,
  RecordHistoryButton,
  LineItemButtons,
  LineItemDetailButton,
  ViewInAdminButton,
  InfractionFlag,
  SelectProductGroup,
  useProductGroupsLoader,
  useSnippetsLoader
} from 'portal/components/RecordsModules/common'
import { ActionStrip } from 'portal/components/RecordsModules/Edit/ActionStrip'

import {
  ExpanderClose,
  ExpanderOpen,
  MainSection,
  MultiColumn,
  MultiColumnRow,
  NameText,
  NegativeMargin,
  StyledLabel,
  SubSection,
  SubSectionHeading,
  TableSection
} from 'portal/components/RecordsModules/common/StyledComponents'

import {
  convertAssignedToArrayToString,
  DateFormatFull,
  imperial
} from 'portal/lib/functions'

import {
  useCheckboxStates,
  useModuleIds,
  useUserProjectRole
} from 'portal/lib/hooks'

import {
  ColourButton,
  Flex,
  icons,
  SmallSpinner,
  SubHeading,
  Spacer,
  ResponsiveTable,
  Text
} from 'portal/lib/primitives'
import { ChevronDown, EditPencil } from 'portal/lib/primitives/icons'
import { convertObjectArrayToString } from 'portal/lib/primitives/ResponsiveTable/common.ts'

import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { useRecordEditing } from 'portal/store/records/editing/useRecordEditing'
import { DeliverySnippets } from './DeliverySnippets'
import { DeliveryProductTag } from './DeliveryProductTag'

export function Delivery({ record }) {
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState()

  const [actionStripRecordID, setActionStripRecordID] = useState([])
  const [recordAssigneeMapping, setRecordAssigneeMapping] = useState({})

  const [showProductGroupsModal, setShowProductGroupsModal] = useState(false)
  const [selectedProductGroupId, setSelectedProductGroupId] = useState(null)
  const [savingProductGroups, setSavingProductGroups] = useState(false)

  const [
    checkedProductIds,
    setCheckedProductId,
    setBulkCheckedProductId,
    clearCheckedProducts
  ] = useCheckboxStates()

  const { accountId, projectId, moduleName } = useModuleIds()

  const { isReadOnly, isProjectAdmin } = useUserProjectRole(
    accountId,
    projectId
  )

  const { data: snippetsData } = useSnippetsLoader()

  const {
    loading: productGroupsLoading,
    error: productGroupsError,
    reload: productGroupsReload
  } = useProductGroupsLoader()

  const dispatch = useDispatch()
  const auth = useSelector(state => state.auth)
  const user = useMemo(
    () => ({
      id: auth?.userUuid || 'unknown',
      name: auth?.userId || 'unknown',
      email: auth?.email || 'unknown'
    }),
    [auth]
  )

  const editing = useRecordEditing()
  const getInfraction = useInfractionGetter()

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )

  const projectIsAlertsEnabled = useSelector(
    state => state.projectDetails.modules?.isAlertsEnabled
  )

  const allProductGroups = useSelector(state => state.productGroups)

  const isImperial = imperial(projectCountry)

  const allProductIds = useMemo(
    () =>
      record.data?.products?.map(product =>
        !product.isRemoved ? product.id : null
      ),
    [record.data.products]
  )

  const openActionStrip = () => {
    if (record && record.recordId) {
      setActionStripRecordID([record.recordId])
    }
  }

  const closeActionStrip = () => {
    setActionStripRecordID([])
  }

  // Prepare assigned to mapping for action strip
  useEffect(() => {
    if (record && record.recordId) {
      // Assuming you have a way to get assignee mapping for the record
      const newObject = {}
      newObject[record.recordId] = record.assignedTo

      setRecordAssigneeMapping(newObject)
    }
  }, [record])

  const handleApplyAssignments = useCallback(() => {
    const EV_ASSIGNEES_UPDATED = 'frontend_record_details_assignees_updated'
    dispatch(
      trackMixpanel(EV_ASSIGNEES_UPDATED, {
        project_id: projectId,
        account_id: accountId,
        record_id: record.id,
        record_type: moduleName
      })
    )
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }, [accountId, dispatch, moduleName, projectId, record.id])

  const dataForProductsTable = useMemo(() => {
    let products = []
    if (record?.data?.products) {
      const originalProducts = [...record.data.products]
      originalProducts.forEach((element, index) => {
        products.push({ originalIndex: index, ...element })
      })
      products = products.filter(p => !p.isDeleted)
    }

    if (record?.dataSourceTracking?.products) {
      products = products.map(product => {
        const dataSourceProduct =
          record.dataSourceTracking.products[product.originalIndex]
        if (dataSourceProduct) {
          return {
            ...product,
            valueAdd: dataSourceProduct
          }
        }
        return product
      })
    }

    // Attach snippets to products
    products = products.map(product => ({
      ...product,
      snippets:
        snippetsData && snippetsData[product.id] ? snippetsData[product.id] : []
    }))
    return products
  }, [record, snippetsData])

  // Set table rows to be expanded
  useEffect(() => {
    if (dataForProductsTable) {
      const keys = dataForProductsTable
        .map(item => {
          if (item.snippets && item.snippets.length > 0 && !item.isRemoved) {
            return item.id
          }
          return null
        })
        .filter(p => p !== null)
      if (keys && keys.length < 3) {
        setExpandedRowKeys(keys)
      } else {
        setExpandedRowKeys([])
      }
    }
  }, [dataForProductsTable])

  const SNIPPET_EXPANDER_TRACKING = 'frontend_dmatches_expander_opened'
  const trackExpanderOpened = useCallback(
    productId => {
      dispatch(
        trackMixpanel(SNIPPET_EXPANDER_TRACKING, {
          account_id: accountId,
          project_id: projectId,
          record_id: record.id,
          product_id: productId,
          user_id: user.id,
          user_name: user.name,
          user_email: user.email
        })
      )
    },
    [accountId, dispatch, projectId, record.id, user]
  )

  const productTableHeaders = useMemo(() => {
    const handleExpanderClick = id => {
      if (expandedRowKeys.includes(id)) {
        setExpandedRowKeys(expandedRowKeys.filter(p => p !== id))
      } else {
        setExpandedRowKeys([...expandedRowKeys, id])
        trackExpanderOpened(id)
      }
    }

    const hasSelectionColumn =
      record.canEdit &&
      !isReadOnly &&
      record.data.products &&
      record.data.products.length > 0

    const columns = [
      {
        key: 'name-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin
              style={{
                margin: hasSelectionColumn
                  ? '0 -0.5rem 0 -0.75rem'
                  : '0 -0.5rem 0 -0.25rem'
              }}
            >
              <ValueAdd
                details={{
                  fieldName: 'name',
                  value: record.name,
                  ...item.name
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'name',
        render: (item, record) => {
          const infractions = getInfraction(
            `data.products[${record.originalIndex}]`,
            {
              isPrefix: true
            }
          )
          return (
            <>
              {record.isRemoved ? item : <NameText>{item}</NameText>}
              {infractions?.length > 0 && (
                <Tooltip
                  key={`infraction-${record.originalIndex}`}
                  title={infractions[0].eventDescriptor}
                  styles={{ body: { width: 'max-content' } }}
                >
                  <span
                    style={{ display: 'inline-block', marginLeft: '0.25rem' }}
                  >
                    <InfractionFlag
                      key={`infraction-flag-${record.originalIndex}`}
                    />
                  </span>
                </Tooltip>
              )}

              <br />
              {expandedRowKeys?.includes(record.id) &&
                record.snippets &&
                record.snippets.length > 0 && (
                  <ExpanderClose onClick={() => handleExpanderClick(record.id)}>
                    <span>See less</span>{' '}
                    <ChevronDown size={'8'} rotate={180} />
                  </ExpanderClose>
                )}
              {!expandedRowKeys?.includes(record.id) &&
                record.snippets &&
                record.snippets.length > 0 && (
                  <ExpanderOpen onClick={() => handleExpanderClick(record.id)}>
                    <span>
                      View {record.snippets.length} design match
                      {record.snippets.length > 1 ? 'es' : ''}
                    </span>{' '}
                    <ChevronDown size={'8'} />
                  </ExpanderOpen>
                )}
            </>
          )
        }
      },
      {
        key: 'givenVolume-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record, index) => {
          if (
            (!record.givenVolume ||
              (Array.isArray(record.givenVolume) &&
                record.givenVolume.length === 0)) &&
            record.calculatedVolume
          ) {
            return (
              <NegativeMargin>
                <ValueAdd
                  details={{
                    fieldName: 'calculatedVolume',
                    value: record.calculatedVolume,
                    type: 'Transformation',
                    transformationType: 'Calculated'
                  }}
                />
              </NegativeMargin>
            )
          }
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'givenVolume',
                  value: record.givenVolume,
                  ...item.givenVolume
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'givenVolume',
        title: isImperial ? (
          'Volume'
        ) : (
          <>
            Volume{' '}
            <span>
              (m<sup>3</sup>)
            </span>
          </>
        ),
        dataIndex: 'givenVolume',
        render: (item, record, index) => {
          if (
            (!item || (Array.isArray(item) && item.length === 0)) &&
            record.calculatedVolume
          ) {
            return convertObjectArrayToString(record.calculatedVolume)
          }
          return convertObjectArrayToString(item)
        }
      },
      {
        key: 'weight-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record, index) => {
          if (
            (!record.weight ||
              (Array.isArray(record.weight) && record.weight.length === 0)) &&
            record.estimatedWeight
          ) {
            return (
              <NegativeMargin>
                <ValueAdd
                  details={{
                    fieldName: 'estimatedWeight',
                    value: record.estimatedWeight,
                    type: 'Transformation',
                    transformationType: 'Calculated'
                  }}
                />
              </NegativeMargin>
            )
          }
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'weight',
                  value: record.weight,
                  ...item.weight
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'weight',
        title: isImperial ? (
          'Weight'
        ) : (
          <>
            Weight <span>(t)</span>
          </>
        ),
        dataIndex: 'weight',
        render: (item, record, index) => {
          if (
            (!item || (Array.isArray(item) && item.length === 0)) &&
            record.estimatedWeight
          ) {
            return convertObjectArrayToString(record.estimatedWeight)
          }
          return convertObjectArrayToString(item)
        }
      },
      {
        key: 'quantity-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'quantity',
                  value: record.quantity,
                  ...item.quantity
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'quantity', title: 'Quantity', dataIndex: 'quantity' },
      {
        key: 'productGroups-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'productGroups',
                  value: record.productGroups,
                  ...item.productGroups
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: '__groups__',
        title: 'Product Group',
        render: item => {
          return <DeliveryProductTag item={item} />
        }
      },
      {
        key: 'embodiedCarbonKgCO2e-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'embodiedCarbonKgCO2e',
                  value: record.embodiedCarbonKgCO2e,
                  ...item.embodiedCarbonKgCO2e
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'embodiedCarbonKgCO2e',
        title: (
          <StyledLabel>
            <div>
              Embodied Carbon{' '}
              <span>
                (kg CO<sub>2</sub>e)
              </span>
            </div>
          </StyledLabel>
        ),
        render: item =>
          item.embodiedCarbonKgCO2e
            ? _.round(item.embodiedCarbonKgCO2e, 2)
            : null
      },
      {
        key: 'actions',
        title: '',
        render: (item, product, index) =>
          !item.isDeleted && record.data?.products[item.originalIndex] ? (
            <ProductControls record={record} index={item.originalIndex} />
          ) : (
            `${item.originalIndex}`
          ),
        width: '130px',
        style: { display: 'flex' },
        align: 'right'
      }
    ]

    // if user is contributor or admin add selection row.
    if (hasSelectionColumn) {
      const selectMenuItems = [
        { label: 'Select all', key: 'selectAll' },
        { label: 'Deselect all', key: 'deselectAll' }
      ]

      const handleSelectMenuClick = e => {
        switch (e.key) {
          // Select all records including ones not currently loaded
          case 'selectAll':
            setCheckAll(true)
            setBulkCheckedProductId(allProductIds, true)
            break
          case 'deselectAll':
            clearCheckedProducts()
            break
        }
      }

      const selectionColumn = {
        title: (
          <Dropdown
            menu={{
              items: selectMenuItems,
              onClick: handleSelectMenuClick
            }}
          >
            <Space>
              <Checkbox
                style={{ pointerEvents: 'none' }}
                indeterminate={indeterminate}
                checked={checkAll}
              ></Checkbox>
              <DownOutlined />
            </Space>
          </Dropdown>
        ),
        dataIndex: 'id',
        className: 'id',
        width: '52px',
        render: (item, product) => (
          <Checkbox
            checked={checkedProductIds.byKey[item]}
            onChange={e => {
              setCheckedProductId(item, e.target.checked)
            }}
            disabled={product.isRemoved}
          />
        )
      }

      columns.unshift(selectionColumn)
    }
    return columns
  }, [
    allProductIds,
    checkAll,
    checkedProductIds.byKey,
    clearCheckedProducts,
    expandedRowKeys,
    getInfraction,
    indeterminate,
    isImperial,
    isReadOnly,
    record,
    setBulkCheckedProductId,
    setCheckedProductId,
    trackExpanderOpened
  ])

  const givenSupplierCertifications = useMemo(() => {
    return record.data?.givenSupplierCertifications?.split(';') ?? []
  }, [record])

  // Update select all indicator based on number selected
  useEffect(() => {
    // Filter out the Select All checkbox so it's not included in the count
    const filteredCheckedProductIds = checkedProductIds.list.filter(
      id => id.length > 1
    )
    if (filteredCheckedProductIds.length === 0) {
      setIndeterminate(false)
      setCheckAll(false)
    } else if (filteredCheckedProductIds.length < allProductIds.length) {
      setIndeterminate(true)
      setCheckAll(false)
    } else {
      setIndeterminate(false)
      setCheckAll(true)
    }
  }, [allProductIds, checkedProductIds.list])

  let assignedTo = ''

  if (record.assignedTo) {
    assignedTo = convertAssignedToArrayToString(record.assignedTo)
  }

  // Tracking product groups updated
  const EV_PRODUCT_GROUPS_UPDATED = 'frontend_records_product_groups_updated'
  const trackProductGroupsUpdated = useCallback(() => {
    dispatch(
      trackMixpanel(EV_PRODUCT_GROUPS_UPDATED, {
        project_id: projectId,
        account_id: accountId,
        record_id: record.id
      })
    )
  }, [accountId, dispatch, projectId, record.id])

  // Updating Product Groups
  const handleUpdateProductGroups = useCallback(() => {
    const onProductGroupSaveComplete = () => {
      productGroupsReload()
      setSavingProductGroups(false)
    }
    // Close and reset modal
    setShowProductGroupsModal(false)
    setSelectedProductGroupId(null)
    setSavingProductGroups(true)

    const baseRecord = _.cloneDeep(record)
    const updatedRecord = produce(baseRecord, draft => {
      draft.data.products.forEach(product => {
        if (product.isDeleted) {
          return
        }
        if (checkedProductIds.byKey[product.id]) {
          product.productGroups = !selectedProductGroupId
            ? []
            : [
                {
                  name: allProductGroups.data.find(
                    group => group.productGroupId === selectedProductGroupId
                  ).name,
                  productGroupId: selectedProductGroupId
                }
              ]
        }
      })
    })

    trackProductGroupsUpdated()

    editing.submitRecord(updatedRecord, onProductGroupSaveComplete, isImperial)
  }, [
    allProductGroups.data,
    checkedProductIds.byKey,
    editing,
    isImperial,
    productGroupsReload,
    record,
    selectedProductGroupId,
    trackProductGroupsUpdated
  ])

  return (
    <>
      <MainSection>
        <ActionStrip
          recordIds={actionStripRecordID}
          recordAssigneeMapping={recordAssigneeMapping}
          onClose={closeActionStrip}
          onApply={handleApplyAssignments}
        />
        <Flex.Row amount={0} verticalCentre>
          <SubHeading
            id="DeliveryDetailHeading"
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Delivery Record
          </SubHeading>

          <Flex.By amount={1} />
          {record.canEdit && (
            <Flex.By amount={0}>
              <ViewInAdminButton
                record={record}
                accountId={accountId}
                moduleName={moduleName}
              />
              {record.qcrAppliedToRaw && (
                <>
                  <Spacer.Tiny />
                  <RecordHistoryButton />
                </>
              )}
              <Spacer.Tiny />
              <EditRecordButton />
              <Spacer.Tiny />
              <RemoveRecordButton isImperial={isImperial} />
            </Flex.By>
          )}
        </Flex.Row>

        <MultiColumn>
          <MultiColumnRow>
            <SubSection style={{ marginTop: '1.25rem' }}>
              <SubSectionHeading>General</SubSectionHeading>
              <DetailRow
                label="Assigned to"
                content={assignedTo}
                testId={'assigned-to'}
                valueAdd={{
                  fieldName: 'assignedTo',
                  ...record.dataSourceTracking?.assignedTo
                }}
                specialAction={
                  isProjectAdmin ? (
                    <Tooltip title="Edit assignees">
                      <ColourButton.BasicBorderless
                        css={{
                          'width': 'fit-content',
                          'padding': '1px 5px',
                          'minHeight': '28px',
                          'color': colours.TEAL,
                          '&:hover': {
                            color: colours.TEAL_HOVER,
                            backgroundColor: colours.LIGHT_TEAL
                          }
                        }}
                        type="button"
                        onClick={openActionStrip}
                      >
                        <EditPencil />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  ) : null
                }
              />

              <DetailRow
                label="Record created"
                content={
                  record.createdAt
                    ? new Date(record.createdAt).toLocaleString(
                        [],
                        DateFormatFull(projectTimeZone)
                      )
                    : null
                }
              />
            </SubSection>

            <SubSection>
              <SubSectionHeading>Delivery</SubSectionHeading>
              <DetailRow
                label="Arrival date and time"
                content={
                  record.data.deliveryArrivalDate
                    ? new Date(record.data.deliveryArrivalDate).toLocaleString(
                        [],
                        DateFormatFull(projectTimeZone)
                      )
                    : null
                }
                valueAdd={{
                  fieldName: 'deliveryArrivalDate',
                  ...record.dataSourceTracking?.deliveryArrivalDate
                }}
              />

              <DetailRow
                label="Exit date and time"
                content={
                  record.data.deliveryExitDate
                    ? new Date(record.data.deliveryExitDate).toLocaleString(
                        [],
                        DateFormatFull(projectTimeZone)
                      )
                    : null
                }
                valueAdd={{
                  fieldName: 'deliveryExitDate',
                  ...record.dataSourceTracking?.deliveryExitDate
                }}
              />

              <DetailRow
                label="Delivery ID"
                content={record.data.deliveryId}
                valueAdd={{
                  fieldName: 'deliveryId',
                  ...record.dataSourceTracking?.deliveryId
                }}
              />

              <DetailRow
                label="Site address"
                content={record.data.deliveryAddressKnown}
                valueAdd={{
                  fieldName: 'deliveryAddressKnown',
                  ...record.dataSourceTracking?.deliveryAddressKnown
                }}
              />

              <DetailRow
                label="Dispatch address"
                content={record.data.dispatchAddressKnown}
                valueAdd={{
                  fieldName: 'dispatchAddressKnown',
                  ...record.dataSourceTracking?.dispatchAddressKnown
                }}
              />

              <DetailRow
                label="Receiver reference"
                content={record.data.receiverReferenceNumber}
                valueAdd={{
                  fieldName: 'receiverReferenceNumber',
                  ...record.dataSourceTracking?.receiverReferenceNumber
                }}
              />
            </SubSection>

            <SubSection>
              <SubSectionHeading>Supplier</SubSectionHeading>

              <DetailRow
                label="Company name"
                content={record.data.knownSupplierName}
                valueAdd={{
                  fieldName: 'knownSupplierName',
                  ...record.dataSourceTracking?.knownSupplierName
                }}
              />

              <DetailRow
                label="Reference"
                content={record.data.supplierReferenceNumber}
                valueAdd={{
                  fieldName: 'supplierReferenceNumber',
                  ...record.dataSourceTracking?.supplierReferenceNumber
                }}
              />

              <DetailRow
                label="Certifications"
                valueAdd={{
                  fieldName: 'givenSupplierCertifications',
                  ...record.dataSourceTracking?.givenSupplierCertifications
                }}
              >
                <Flex.Col>
                  {record.data.knownSupplierCertifications?.map(
                    ({ name, resourceURI }) => (
                      <CertificationLinkText key={name + resourceURI}>
                        {resourceURI ? (
                          <a
                            href={resourceURI}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {name}
                          </a>
                        ) : (
                          name
                        )}
                      </CertificationLinkText>
                    )
                  )}
                  {givenSupplierCertifications.map(cert => (
                    <div key={cert}>{cert}</div>
                  ))}
                </Flex.Col>
              </DetailRow>

              <DetailRow
                label="Trade contractor"
                content={record.data.tradeContractor}
                valueAdd={{
                  fieldName: 'tradeContractor',
                  ...record.dataSourceTracking?.tradeContractor
                }}
              />

              <DetailRow
                label="Vehicle registration"
                content={record.data.vehicleRegistration}
                valueAdd={{
                  fieldName: 'vehicleRegistration',
                  ...record.dataSourceTracking?.vehicleRegistration
                }}
              />

              <DetailRow
                label="External reference"
                content={record.data.clientReference}
                valueAdd={{
                  fieldName: 'clientReference',
                  ...record.dataSourceTracking?.clientReference
                }}
              />

              <DetailRow
                label="Notes"
                content={record.data.notes}
                valueAdd={{
                  fieldName: 'notes',
                  ...record.dataSourceTracking?.notes
                }}
              />
            </SubSection>

            <SubSection>
              <SubSectionHeading>Journey</SubSectionHeading>
              <DetailRow
                label="Journey distance (mi)"
                content={
                  record.data.journey?.distanceMiles
                    ? _.round(record.data.journey.distanceMiles, 2).toString()
                    : null
                }
                tooltip="Journey distance represents the laden journey only"
                valueAdd={{
                  fieldName: 'distanceMiles',
                  ...record.dataSourceTracking?.journey
                }}
              />
              <DetailRow
                label={
                  <span>
                    Laden journey emissions (kg CO<sub>2</sub>e)
                  </span>
                }
                content={
                  record.data.journey?.ladenEmissionsKgCO2e
                    ? _.round(
                        record.data.journey.ladenEmissionsKgCO2e,
                        2
                      ).toString()
                    : null
                }
                valueAdd={{
                  fieldName: 'ladenEmissionsKgCO2e',
                  ...record.dataSourceTracking?.journey
                }}
              />
              <DetailRow
                label={
                  <span>
                    Unladen journey emissions (kg CO<sub>2</sub>e)
                  </span>
                }
                content={
                  record.data.journey?.unladenEmissionsKgCO2e
                    ? _.round(
                        record.data.journey.unladenEmissionsKgCO2e,
                        2
                      ).toString()
                    : null
                }
                valueAdd={{
                  fieldName: 'unladenEmissionsKgCO2e',
                  ...record.dataSourceTracking?.journey
                }}
              />
            </SubSection>
          </MultiColumnRow>
          {projectIsAlertsEnabled && (
            <AlertsPanel alerts={record.alerts} canEdit={record.canEdit} />
          )}
        </MultiColumn>
      </MainSection>

      <MainSection>
        <AttachmentsHeader record={record} />
        <AttachmentsSection record={record} attachments={record.attachments} />
      </MainSection>

      <MainSection>
        <Flex.Row
          amount={0}
          verticalCentre
          style={{ justifyContent: 'space-between' }}
        >
          <SubHeading
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Products
          </SubHeading>

          {record.canEdit &&
            !isReadOnly &&
            record.data.products &&
            record.data.products.length > 0 && (
              <ColourButton.Basic
                onClick={setShowProductGroupsModal}
                disabled={
                  !checkedProductIds || checkedProductIds.list.length < 1
                }
              >
                <Tooltip
                  title={
                    checkedProductIds && checkedProductIds.list.length > 0
                      ? null
                      : 'Select at least 1 product to update product group'
                  }
                  placement="right"
                >
                  Update product group
                </Tooltip>
              </ColourButton.Basic>
            )}
        </Flex.Row>

        <TableSection>
          <Table
            id="products-table"
            dataSource={dataForProductsTable ?? []}
            rowKey={row => row.id}
            columns={productTableHeaders}
            size="small"
            style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}
            pagination={false}
            locale={{ emptyText: 'No products' }}
            loading={{
              spinning: savingProductGroups,
              indicator: (
                <div>
                  <SmallSpinner />
                </div>
              )
            }}
            onRow={(record, index) => ({
              style: record.isRemoved
                ? {
                    textDecoration: 'line-through',
                    color: colours.BLUEY_GREY
                  }
                : {},
              className: expandedRowKeys?.includes(record.id)
                ? 'ant-table-expanded-row'
                : ''
            })}
            expandable={{
              expandedRowRender: record => (
                <DeliverySnippets
                  productId={record.id}
                  snippets={record.snippets}
                />
              ),
              expandedRowKeys: expandedRowKeys,
              rowExpandable: record =>
                record.snippets && record.snippets.length > 0,
              showExpandColumn: false
            }}
          />
        </TableSection>
      </MainSection>

      <Modal
        title={<Text dim>Update product group</Text>}
        open={showProductGroupsModal}
        closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
        onCancel={() => setShowProductGroupsModal(false)}
        footer={null}
        transitionName=""
        wrapClassName="common-modal"
      >
        <h2>
          Set product group for the selected product
          {checkedProductIds && checkedProductIds.list.length > 1 ? 's' : ''}
        </h2>
        <Row style={{ margin: '20px 0 10px 0' }}>
          <Col flex="auto">
            {!productGroupsLoading && !productGroupsError && (
              <SelectProductGroup
                productGroups={allProductGroups.data}
                selectedProductGroupId={selectedProductGroupId}
                setSelectedProductGroupId={setSelectedProductGroupId}
              />
            )}
          </Col>
        </Row>
        <Row style={{ margin: '20px 0 0 0' }}>
          <Col flex="auto">
            <p className="warning">
              Warning: Updating product groups will remove any estimates and
              calculated carbon.
            </p>
          </Col>
        </Row>
        <Row style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Col>
            <ColourButton.Basic
              onClick={() => setShowProductGroupsModal(false)}
            >
              Cancel
            </ColourButton.Basic>
          </Col>
          <Spacer.Tiny />
          <Col>
            <ColourButton.Action onClick={handleUpdateProductGroups}>
              Update product
              {checkedProductIds && checkedProductIds.list.length > 1
                ? 's'
                : ''}
            </ColourButton.Action>
          </Col>
        </Row>
      </Modal>
    </>
  )
}

Delivery.propTypes = {
  record: PropTypes.object.isRequired
}

const ProductControls = ({ record, index }) => {
  const getInfraction = useInfractionGetter()

  const item = record.data.products[index]
  const itemValueAdds = record.dataSourceTracking?.products[index]

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )

  const isImperial = imperial(projectCountry)

  return (
    <div style={{ display: 'inline-flex' }}>
      <LineItemDetailButton
        title={`Product Details`}
        detail={
          <ResponsiveTable.AutoCard
            headers={productDetailHeaders(projectTimeZone, isImperial)}
            index={index}
            item={item}
            itemValueAdds={itemValueAdds}
            titleKey="name"
            removed={item.isRemoved}
            renderIcon={key => {
              const infraction = getInfraction(`data.products[${index}].${key}`)
              return infraction ? (
                <Tooltip placement="right" title={infraction.eventDescriptor}>
                  <span>
                    <InfractionFlag />
                  </span>
                </Tooltip>
              ) : null
            }}
            controls={
              <LineItemButtons
                record={record}
                index={index}
                isImperial={isImperial}
              />
            }
          />
        }
      />

      <Spacer.Tiny />
      <LineItemButtons record={record} index={index} isImperial={isImperial} />
    </div>
  )
}

ProductControls.propTypes = {
  record: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
}

const productDetailHeaders = (projectTimeZone, isImperial) => [
  { key: 'name', label: 'Given Name' },
  {
    key: 'productionTime',
    label: 'Produced at',
    render: item =>
      item.productionTime ? (
        <Text>
          {new Date(item.productionTime).toLocaleString(
            [],
            DateFormatFull(projectTimeZone)
          )}
        </Text>
      ) : null
  },
  {
    key: 'usedAtTime',
    label: 'Used at',
    render: item =>
      item.usedAtTime ? (
        <Text>
          {new Date(item.usedAtTime).toLocaleString(
            [],
            DateFormatFull(projectTimeZone)
          )}
        </Text>
      ) : null
  },
  { key: 'height', label: isImperial ? 'Height' : 'Height (m)' },
  { key: 'width', label: isImperial ? 'Width' : 'Width (m)' },
  { key: 'length', label: isImperial ? 'Length' : 'Length (m)' },
  {
    key: 'givenVolume',
    label: isImperial ? (
      'Volume'
    ) : (
      <>
        Volume (m<sup>3</sup>)
      </>
    )
  },
  { key: 'weight', label: isImperial ? 'Weight' : 'Weight (t)' },
  { key: 'quantity', label: 'Quantity (units)' },
  {
    key: 'givenCertifications',
    label: 'Certifications',
    render: item => {
      const certifications = (item.givenCertifications ?? []).filter(Boolean)
      if (!certifications?.length) {
        return null
      }
      return (
        <div>
          {certifications.map((certification, index) => (
            <Text
              key={index}
              style={
                index < certifications.length - 1
                  ? { marginRight: '0.25rem' }
                  : {}
              }
            >
              {certification}
            </Text>
          ))}
        </div>
      )
    }
  },
  // 20/01/2025 - Comment out knownCertifications, but we may reinstate it later
  // {
  //   key: 'knownCertifications',
  //   label: 'Known Certifications',
  //   render: item => {
  //     const certifications = item.knownCertifications ?? []
  //     if (!certifications?.length) {
  //       return null
  //     }

  //     return (
  //       <div>
  //         {certifications.map(({ name, resourceURI }, index) => (
  //           <CertificationLinkText key={index}>
  //             {resourceURI ? (
  //               <a href={resourceURI} target="_blank" rel="noreferrer">
  //                 {name}
  //               </a>
  //             ) : (
  //               name
  //             )}
  //           </CertificationLinkText>
  //         ))}
  //       </div>
  //     )
  //   }
  // },
  {
    key: 'embodiedCarbonKgCO2e',
    label: (
      <span>
        Embodied carbon (kg CO<sub>2</sub>e)
      </span>
    ),
    render: item =>
      item.embodiedCarbonKgCO2e ? _.round(item.embodiedCarbonKgCO2e, 2) : null
  }
]

const CertificationLinkText = styled(Text)`
  margin-right: 0.25rem;
  :first-child {
    margin-top: 0;
  }
`
